import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {AuthService} from "../../../../services/auth.service";
import {
  allNotes,
  clearNotes,
  createNote,
  deleteNote,
  editNote,
  showMoreNotes,
} from "../../../../state/search/search.actions";
import {
  selectAllNotesFailure,
  selectAllNotesSuccess,
  selectCreateNoteFailure,
  selectCreateNoteSuccess,
  selectDeleteNoteFailure,
  selectDeleteNoteSuccess,
  selectEditNoteFailure,
  selectEditNoteSuccess,
  selectShowMoreNotesFailure,
  selectShowMoreNotesSuccess
} from "../../../../state/search/search.selectors";
import {filter, Observable, Subject, Subscription, take} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../state/app.state";

import Swal from "sweetalert2";
import {EventTypes} from "../../../../event-types";
import {SharedService} from "../../../../services/shared.service";
import {animate, style, transition, trigger} from "@angular/animations";
import * as UiActions from "../../../../state/ui/ui.actions";
import {takeUntil} from "rxjs/operators";


@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  animations: [
    trigger('fadeFromRight', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(20px)'
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          transform: 'translateX(0)'
        }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({opacity: 0}),  // Initial state: invisible
        animate('0.2s', style({opacity: 1}))  // Final state: visible
      ])
    ])
  ]
})
export class NotesComponent implements OnInit, OnChanges, OnDestroy {

  @Input() aside$: Observable<boolean>;
  @Input() report: any;
  @Output() dataEmitter: EventEmitter<any> = new EventEmitter();

  // User and state
  user: any;

  // Notes data
  notes: any[] = [];
  note: string = '';
  edited_note_text: string = '';
  total_notes: number = 0;
  page_notes: number = 1;
  added_notes: number = 0;
  deleted_notes: number = 0;

  // Notes summary
  notes_summary: any = null;
  isLoadedSummary: boolean = false;

  // UI state
  isLoadedNotes: boolean = true;
  show_more_notes: boolean = false;
  edit_note: boolean[] = [];

  // Cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private sharedService: SharedService,
  ) {
  }

  ngOnInit(): void {
    // Clear previous notes state
    this.store.dispatch(clearNotes());

    // Listen for notes summary events from WebSocket
    this.sharedService.eventEmitter
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.type === EventTypes.LOAD_NOTES_SUMMARY) {
          this.handleNotesSummaryEvent(event.data);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When report Input changes, load notes if we have both user and report
    if (changes['report']) {
      // Clear previous report's data immediately
      this.clearComponentState();

      // Clear store state
      this.store.dispatch(clearNotes());

      // Load new report's notes if we have a valid report ID
      if (this.report?.id) {
        // Subscribe to authenticated user if not already subscribed
        this.authService.authUser()
          .pipe(take(1))
          .subscribe(user => {
            this.user = user;
            if (user) {
              this.getNotes();
              this.checkForNotesSummary();
            }
          });
      }
    }
  }

  ngOnDestroy(): void {
    // Clear component state on destroy
    this.clearComponentState();
    // Clear store state
    this.store.dispatch(clearNotes());

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Clear all component state
   * Called when switching reports or destroying component
   */
  private clearComponentState(): void {
    this.notes = [];
    this.note = '';
    this.edited_note_text = '';
    this.total_notes = 0;
    this.page_notes = 1;
    this.added_notes = 0;
    this.deleted_notes = 0;
    this.notes_summary = null;
    this.isLoadedSummary = false;
    this.isLoadedNotes = true;
    this.show_more_notes = false;
    this.edit_note = [];
  }

  /**
   * Handle notes summary event from WebSocket
   */
  private handleNotesSummaryEvent(data: any): void {
    try {
      // Try different possible data structures
      let summaryContent = null;

      if (data?.response?.response?.message?.content) {
        summaryContent = data.response.response.message.content;
      } else if (data?.response?.message?.content) {
        summaryContent = data.response.message.content;
      } else if (data?.message?.content) {
        summaryContent = data.message.content;
      } else if (data?.content) {
        summaryContent = data.content;
      } else if (data?.response) {
        summaryContent = data.response;
      }

      if (summaryContent) {
        // Parse if it's a string
        if (typeof summaryContent === 'string') {
          this.notes_summary = JSON.parse(summaryContent);
        } else {
          this.notes_summary = summaryContent;
        }
        this.isLoadedSummary = true;
      } else {
        console.warn('[Notes] Could not extract summary content from event data', data);
      }
    } catch (error) {
      console.error('[Notes] Failed to parse notes summary', error);
      this.notes_summary = null;
      this.isLoadedSummary = false;
    }
  }

  /**
   * Check if notes summary already exists in report insights
   */
  private checkForNotesSummary(): void {
    // Check insights structure for notes_summary
    if (this.report?.insights?.notes_summary) {
      const notesSummary = this.report.insights.notes_summary;

      if (notesSummary.status === 'finished' && notesSummary.response) {
        this.handleNotesSummaryEvent({ response: notesSummary.response });
        return;
      }
    }
  }


  addNote() {
    this.isLoadedNotes = false;
    this.store.dispatch(createNote({search_id: this.report.id, user_id: this.report.user.id, content: this.note}));

    const createNoteSuccess$ = this.store.select(selectCreateNoteSuccess);
    const createNoteFailure$ = this.store.select(selectCreateNoteFailure);

    let createNoteSuccessSubscription: Subscription = null;
    let createNoteFailureSubscription: Subscription = null;


    createNoteSuccessSubscription = createNoteSuccess$.pipe(
      filter(data => data.loading_note_create != true),
      take(1)).subscribe({
      next: (data) => {
        this.added_notes += 1;
        this.edit_note.push(false); // Add false for the new note
        this.notes = [data.created_note, ...this.notes];
        this.total_notes += 1;

        // Reset summary since notes changed - will be regenerated by backend
        this.isLoadedSummary = false;
        this.notes_summary = null;

        this.note = '';

        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);

        if (createNoteSuccessSubscription) {
          createNoteSuccessSubscription.unsubscribe();

        }
      }, error: (error) => {

        console.error(' create  Notes error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (createNoteSuccessSubscription) {
          createNoteSuccessSubscription.unsubscribe();
        }
      }
    });

    createNoteFailureSubscription = createNoteFailure$.pipe(filter(failure => failure !== null), take(1)).subscribe({
      next: (error) => {


        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);


        if (createNoteFailureSubscription) {
          createNoteFailureSubscription.unsubscribe();
        }
      }, error: (error) => {

        console.error(' create notes  handling error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (createNoteFailureSubscription) {
          createNoteFailureSubscription.unsubscribe();
        }
      }
    });

  }

  /**
   * Format date for display
   */
  formatDate(date: string | Date): string {
    return new DatePipe('en-US').transform(date, 'MMM d') || '';
  }


  getNotes() {

    this.isLoadedNotes = false;
    this.store.dispatch(allNotes({search_id: this.report.id, user_id: this.report.user.id}));

    const getAllNotesSuccess$ = this.store.select(selectAllNotesSuccess);
    const getAllNotesFailure$ = this.store.select(selectAllNotesFailure);

    let getAllNotesSuccessSubscription: Subscription = null;
    let getAllNotesFailureSubscription: Subscription = null;


    getAllNotesSuccessSubscription = getAllNotesSuccess$.pipe(
      filter(data => data.notes != null),
      take(1)).subscribe({
      next: (data) => {


        this.notes = data.notes.data
        this.total_notes = data.notes.total;
        this.edit_note = Array(this.total_notes).fill(0);

        if (this.notes.length < data.notes.total) {
          this.show_more_notes = true;
          this.page_notes = 1;
        }
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);

        if (getAllNotesSuccessSubscription) {
          getAllNotesSuccessSubscription.unsubscribe();

        }
      }, error: (error) => {

        console.error(' Get All Notes error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (getAllNotesSuccessSubscription) {
          getAllNotesSuccessSubscription.unsubscribe();
        }
      }
    });

    getAllNotesFailureSubscription = getAllNotesFailure$.pipe(filter(failure => failure !== null), take(1)).subscribe({
      next: (error) => {


        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);


        if (getAllNotesFailureSubscription) {
          getAllNotesFailureSubscription.unsubscribe();
        }
      }, error: (error) => {

        console.error('Get all notes error handling error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (getAllNotesFailureSubscription) {
          getAllNotesFailureSubscription.unsubscribe();
        }
      }
    });

  }

  deleteNote(note_id, search_id) {

    this.isLoadedNotes = false;
    this.store.dispatch(deleteNote({note_id, search_id}));

    const deleteNoteSuccess$ = this.store.select(selectDeleteNoteSuccess);
    const deleteNoteFailure$ = this.store.select(selectDeleteNoteFailure);

    let deleteNoteSuccessSubscription: Subscription = null;
    let deleteNoteFailureSubscription: Subscription = null;

    // Modify the filter that's why it does not work.
    deleteNoteSuccessSubscription = deleteNoteSuccess$.pipe(
      filter(data => data.loading_note_delete != true),
      take(1)).subscribe({
      next: (data) => {
        this.deleted_notes += 1;
        this.notes = this.notes.filter(item => item.id !== data.deleted_note.id);
        this.total_notes -= 1;

        // Reset summary since notes changed - will be regenerated by backend
        this.isLoadedSummary = false;
        this.notes_summary = null;

        this.edit_note.pop();

        // if (data.notes.total - this.deleted_notes === this.notes.length) this.show_more_notes = false;
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);

        if (deleteNoteSuccessSubscription) {
          deleteNoteSuccessSubscription.unsubscribe();

        }
      }, error: (error) => {

        console.error(' Delete note error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (deleteNoteSuccessSubscription) {
          deleteNoteSuccessSubscription.unsubscribe();
        }
      }
    });

    deleteNoteFailureSubscription = deleteNoteFailure$.pipe(filter(failure => failure !== null), take(1)).subscribe({
      next: (error) => {


        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);


        if (deleteNoteFailureSubscription) {
          deleteNoteFailureSubscription.unsubscribe();
        }
      }, error: (error) => {

        console.error('Delete note error handling error:', error);
        setTimeout(() => {
          this.isLoadedNotes = true;
        }, 500);
        if (deleteNoteFailureSubscription) {
          deleteNoteFailureSubscription.unsubscribe();
        }
      }
    });

  }

  deleteNotePopUp(note) {

    Swal.fire({
      title: 'Are you sure you want to delete this note?',
      html: note.content + " <br> <br> You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#802cfa',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // Call the method to delete the item
        this.deleteNote(note, note.search_id);
      }
    });
  }

  showMoreNotes() {

    this.page_notes += 1;
    this.store.dispatch(showMoreNotes({search_id: this.report.id, page: this.page_notes}));

    const showMoreNotesSuccess$ = this.store.select(selectShowMoreNotesSuccess);
    const showMoreNotesFailure$ = this.store.select(selectShowMoreNotesFailure);

    let showMoreNotesSuccessSubscription: Subscription = null;
    let showMoreNotesFailureSubscription: Subscription = null;


    showMoreNotesSuccessSubscription = showMoreNotesSuccess$.pipe(filter(data => data.notes != null), take(1)).subscribe({
      next: (data) => {

        this.notes = this.notes.concat(data.notes.data);

        // Change the total value of the pagination if user added a deal

        if (data.notes.total + this.added_notes - this.deleted_notes === this.notes.length) this.show_more_notes = false;

        if (showMoreNotesSuccessSubscription) {
          showMoreNotesSuccessSubscription.unsubscribe();

        }
      }, error: (error) => {

        console.error('Show More Notes  error :', error);

        if (showMoreNotesSuccessSubscription) {
          showMoreNotesSuccessSubscription.unsubscribe();
        }
      }
    });

    showMoreNotesFailureSubscription = showMoreNotesFailure$.pipe(filter(failure => failure !== null), take(1)).subscribe({
      next: (error) => {


        console.log(error);
        if (showMoreNotesFailureSubscription) {
          showMoreNotesFailureSubscription.unsubscribe();
        }
      }, error: (error) => {
        // Handle error in error handling
        console.error('Show More Notes handling error:', error);

        if (showMoreNotesFailureSubscription) {
          showMoreNotesFailureSubscription.unsubscribe();
        }
      }
    });

  }


  editNote(note, edited_note_text) {

    if(note.content.trimEnd() != edited_note_text.trimEnd()) {
      this.isLoadedNotes = false;
      this.store.dispatch(editNote({note: note, edited_note_text: edited_note_text}));

      const editNoteSuccess$ = this.store.select(selectEditNoteSuccess);
      const editNoteFailure$ = this.store.select(selectEditNoteFailure);

      let editNoteSuccessSubscription: Subscription = null;
      let editNoteFailureSubscription: Subscription = null;


      editNoteSuccessSubscription = editNoteSuccess$.pipe(
        filter(data => data.loading_note_edit != true),
        take(1)).subscribe({
        next: (data) => {
          this.notes = this.notes.filter(item => item.id !== data.edited_note.id);
          this.notes = [data.edited_note, ...this.notes];

          // Reset summary since notes changed - will be regenerated by backend
          this.isLoadedSummary = false;
          this.notes_summary = null;

          this.note = '';

          setTimeout(() => {
            this.isLoadedNotes = true;
          }, 500);

          if (editNoteSuccessSubscription) {
            editNoteSuccessSubscription.unsubscribe();

          }
        }, error: (error) => {

          console.error(' Edit Notes error:', error);
          setTimeout(() => {
            this.isLoadedNotes = true;
          }, 500);
          if (editNoteSuccessSubscription) {
            editNoteSuccessSubscription.unsubscribe();
          }
        }
      });

      editNoteFailureSubscription = editNoteFailure$.pipe(filter(failure => failure !== null), take(1)).subscribe({
        next: (error) => {


          setTimeout(() => {
            this.isLoadedNotes = true;
          }, 500);


          if (editNoteFailureSubscription) {
            editNoteFailureSubscription.unsubscribe();
          }
        }, error: (error) => {

          console.error(' edit notes  handling error:', error);
          setTimeout(() => {
            this.isLoadedNotes = true;
          }, 500);
          if (editNoteFailureSubscription) {
            editNoteFailureSubscription.unsubscribe();
          }
        }
      });
    }
  }

  /**
   * Toggle edit mode for a note
   */
  toggleEditNote(i: number): void {
    // Close all other edit modes
    this.edit_note = this.edit_note.fill(false);
    // Open this edit mode
    this.edit_note[i] = true;
    this.edited_note_text = this.notes[i]?.content || '';
  }

  /**
   * Close edit mode for a note
   */
  toggleEditNoteFinish(i: number): void {
    this.edit_note[i] = false;
    this.edited_note_text = '';
  }

  closeAside(): void {
    // Clear component state when closing aside
    this.clearComponentState();
    // Clear store state
    this.store.dispatch(clearNotes());
    // Close the aside
    this.store.dispatch(UiActions.toggleAside({ isOpen: false }));
  }

}

